import { SkeletonCircle } from "./SkeletonCircle";
import { SkeletonRectangle } from "./SkeletonRectangle";
import { SkeletonText } from "./SkeletonText";
import { SkeletonPill } from "./SkeletonPill";

/**
 * Skeleton component for displaying loading placeholders.
 *
 * Features:
 * - Multiple skeleton variants for different content types
 * - Animated pulsing effect for loading indication
 * - Accessible with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Customizable styling through className and style props
 * - Progress bar role for screen readers
 * - Compound component pattern with dot exports
 *
 * @example
 * <Skeleton.Text variant="body" rows={3} />
 * <Skeleton.Circle diameter="40px" />
 * <Skeleton.Rectangle height="100px" />
 * <Skeleton.Pill width="120px" />
 */
export const Skeleton = {
  /**
   * SkeletonText component for displaying text loading placeholders.
   *
   * Features:
   * - Text skeleton placeholder with body and headline variants
   * - Configurable number of rows for body variant
   * - Animated pulsing effect for loading indication
   * - Accessible with proper ARIA attributes
   * - Layout utility props for positioning and spacing
   * - Customizable styling through className and style props
   * - Progress bar role for screen readers
   * - Different line heights for body vs headline variants
   * - Last line shorter for body variant to simulate natural text
   *
   * @example
   * <Skeleton.Text variant="body" rows={3} />
   *
   * <Skeleton.Text
   *   variant="headline"
   *   className="custom-skeleton"
   *   aria-label="Loading title"
   * />
   */
  Text: SkeletonText,
  /**
   * SkeletonCircle component for displaying circular loading placeholders.
   *
   * Features:
   * - Circular skeleton placeholder with customizable diameter
   * - Animated pulsing effect for loading indication
   * - Accessible with proper ARIA attributes
   * - Layout utility props for positioning and spacing
   * - Customizable styling through className and style props
   * - Progress bar role for screen readers
   *
   * @example
   * <Skeleton.Circle diameter="40px" />
   *
   * <Skeleton.Circle
   *   diameter="60px"
   *   className="custom-skeleton"
   *   aria-label="Loading avatar"
   * />
   */
  Circle: SkeletonCircle,
  /**
   * SkeletonRectangle component for displaying rectangular loading placeholders.
   *
   * Features:
   * - Rectangular skeleton placeholder with customizable height
   * - Animated pulsing effect for loading indication
   * - Accessible with proper ARIA attributes
   * - Layout utility props for positioning and spacing
   * - Customizable styling through className and style props
   * - Progress bar role for screen readers
   * - Medium border radius for consistent styling
   *
   * @example
   * <Skeleton.Rectangle height="100px" />
   *
   * <Skeleton.Rectangle
   *   height="2rem"
   *   className="custom-skeleton"
   *   aria-label="Loading image"
   * />
   */
  Rectangle: SkeletonRectangle,
  /**
   * SkeletonPill component for displaying pill-shaped loading placeholders.
   *
   * Features:
   * - Pill-shaped skeleton placeholder with customizable width
   * - Animated pulsing effect for loading indication
   * - Accessible with proper ARIA attributes
   * - Layout utility props for positioning and spacing
   * - Customizable styling through className and style props
   * - Progress bar role for screen readers
   * - Large border radius for pill appearance
   * - Fixed height for consistent styling
   *
   * @example
   * <Skeleton.Pill width="120px" />
   *
   * <Skeleton.Pill
   *   width="8rem"
   *   className="custom-skeleton"
   *   aria-label="Loading tag"
   * />
   */
  Pill: SkeletonPill,
};
